import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

export interface UploadDialogState {
  visible: boolean;
  fileId?: number;
  folderId?: number;
  editFolderId?: number;
  existingData?: any;
  existingFolderData?: any;
  firstOpenAfterReload?: boolean;
  /** 1 = Personal (My Files), 2 = Department (Department/Org Assets). */
  libraryType?: number;
  /** Department id to stamp on root-level Department uploads/creates. */
  departmentId?: number | null;
  /** When true, a successful file edit also reopens the active workflow level. */
  resubmitWorkflowOnUpdate?: boolean;
  returnComment?: string;
  /**
   * When true (file-manager scoped upload), hide My Files / Department Assets toggle.
   * Sidebar "common" upload keeps the toggle by not setting this flag.
   */
  folderPickerSourceLocked?: boolean;
  autoPopulate?: {
    title: string;
    selectedEvent: any;
    selectedLocation?: any;
    date: Date;
    /** ISO strings for event range (start/end); optional for restore after close */
    eventDateRangeStart?: string | null;
    eventDateRangeEnd?: string | null;
    tags: any[];
    markAsSecure: boolean;
    shareType: 'internal' | 'external';
    sharePermissionLevel?: any[];
    shareUserIds: number[];
    shareEmails: string[];
    shareExpireDate: Date | null;
    selectedFileType?: 'document' | 'image' | 'video' | null;
    selectedCategoryId?: number | null;
    categoryLocked?: boolean;
  };
}

export interface UploadSuccessTarget {
  folderId: number;
  libraryType: number;
}

type UploadDialogLibraryContext = {
  libraryType?: number;
  departmentId?: number | null;
  folderPickerSourceLocked?: boolean;
  resubmitWorkflowOnUpdate?: boolean;
  returnComment?: string;
};

@Injectable({
  providedIn: 'root'
})
export class UploadMediaDialogService {
  private dialogVisibleSubject = new BehaviorSubject<UploadDialogState>({
    visible: false
  });
  public dialogVisible$: Observable<UploadDialogState> = this.dialogVisibleSubject.asObservable();

  private readonly uploadSuccessSubject = new Subject<UploadSuccessTarget>();
  readonly uploadSuccess$: Observable<UploadSuccessTarget> = this.uploadSuccessSubject.asObservable();

  private pendingMetadata: UploadDialogState['autoPopulate'] | null = null;
  private pendingSelectedFiles: File[] = [];
  private hasOpenedSinceReload = false;

  constructor() {}

  /**
   * Opens the upload media dialog
   * @param fileId Optional file ID for edit mode
   * @param folderId Current folder ID where upload was clicked (0 for root level)
   * @param autoPopulate Optional metadata to auto-populate the form
   */
  openDialog(
    fileId?: number,
    folderId?: number,
    autoPopulate?: UploadDialogState['autoPopulate'],
    editFolderId?: number,
    libraryContext?: UploadDialogLibraryContext,
    initialData?: Pick<UploadDialogState, 'existingData' | 'existingFolderData'>
  ): void {
    const firstOpenAfterReload = !this.hasOpenedSinceReload;
    this.hasOpenedSinceReload = true;
    const resolvedLibraryContext = this.resolveLibraryContext(libraryContext);
    this.dialogVisibleSubject.next({
      visible: true,
      fileId: fileId || 0,
      folderId: folderId !== undefined && folderId !== null ? folderId : 0,
      editFolderId: editFolderId || 0,
      existingData: initialData?.existingData ?? null,
      existingFolderData: initialData?.existingFolderData ?? null,
      firstOpenAfterReload,
      libraryType: resolvedLibraryContext.libraryType,
      departmentId: resolvedLibraryContext.departmentId,
      folderPickerSourceLocked: resolvedLibraryContext.folderPickerSourceLocked,
      resubmitWorkflowOnUpdate: resolvedLibraryContext.resubmitWorkflowOnUpdate,
      returnComment: resolvedLibraryContext.returnComment,
      autoPopulate: autoPopulate || this.pendingMetadata || undefined
    });
    
    // Clear pending metadata after using it
    if (autoPopulate || this.pendingMetadata) {
      this.pendingMetadata = null;
    }
  }

  
  closeDialog(): void {
    this.dialogVisibleSubject.next({
      visible: false
    });
  }

  notifyUploadSuccess(folderId: number, libraryType?: number): void {
    const id = Number.isFinite(Number(folderId)) ? Number(folderId) : 0;
    const normalizedLibraryType = Number(libraryType);
    this.uploadSuccessSubject.next({
      folderId: id,
      libraryType:
        Number.isFinite(normalizedLibraryType) && normalizedLibraryType > 0
          ? normalizedLibraryType
          : 1,
    });
  }

  isDialogVisible(): boolean {
    return this.dialogVisibleSubject.value.visible;
  }

  
  savePendingMetadata(metadata: UploadDialogState['autoPopulate']): void {
    this.pendingMetadata = metadata;
  }

 
  getPendingMetadata(): UploadDialogState['autoPopulate'] | null {
    return this.pendingMetadata;
  }

  
  reopenWithAutoPopulate(autoPopulate: UploadDialogState['autoPopulate']): void {
    const currentState = this.dialogVisibleSubject.value;
    this.openDialog(
      currentState.fileId || 0,
      currentState.folderId !== undefined && currentState.folderId !== null ? currentState.folderId : 0,
      autoPopulate,
      currentState.editFolderId || 0,
      {
        libraryType: currentState.libraryType,
        departmentId: currentState.departmentId ?? null,
        folderPickerSourceLocked: currentState.folderPickerSourceLocked === true,
        resubmitWorkflowOnUpdate: currentState.resubmitWorkflowOnUpdate === true,
        returnComment: currentState.returnComment
      },
      {
        existingData: currentState.existingData ?? null,
        existingFolderData: currentState.existingFolderData ?? null
      }
    );
  }

  setPendingSelectedFiles(files: File[]): void {
    this.pendingSelectedFiles = files ? [...files] : [];
  }

  consumePendingSelectedFiles(): File[] {
    const files = [...this.pendingSelectedFiles];
    this.pendingSelectedFiles = [];
    return files;
  }

  private resolveLibraryContext(libraryContext?: UploadDialogLibraryContext): Required<UploadDialogLibraryContext> {
    const explicitLibraryType = Number(libraryContext?.libraryType);
    const libraryType =
      Number.isFinite(explicitLibraryType) && explicitLibraryType > 0
        ? explicitLibraryType
        : this.inferLibraryTypeFromLocation();
    const departmentId =
      libraryType === 2
        ? this.normalizeDepartmentId(libraryContext?.departmentId)
        : null;

    return {
      libraryType,
      departmentId,
      folderPickerSourceLocked: libraryContext?.folderPickerSourceLocked === true,
      resubmitWorkflowOnUpdate: libraryContext?.resubmitWorkflowOnUpdate === true,
      returnComment: String(libraryContext?.returnComment ?? '').trim()
    };
  }

  private inferLibraryTypeFromLocation(): number {
    const path = typeof window !== 'undefined' ? String(window.location?.pathname || '').toLowerCase() : '';
    return path.includes('/main/home/dept-assets') ? 2 : 1;
  }

  private normalizeDepartmentId(value: unknown): number | null {
    const departmentId = Number(value);
    return Number.isFinite(departmentId) && departmentId > 0 ? departmentId : null;
  }
}
